import * as React from 'react';
import { UploadedFile } from '../../../../../common/domain/upload';
import { createVideoThumbnail } from '../../../../../common/create-video-thumbnail-mutation';
import { CircularProgress, Typography } from '@mui/material';
import { config } from '../../../../../common/config';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from '../../../../../common/components/ApolloError/GraphQLError';
import { uploadFile } from '../../../../../common/upload';
import withSession, { WithSession } from '../../../../../common/Session/withSession';
import { COLORS } from '../../../../../common/Styleguide/Common/colors';
import { StyledButton } from '../../../../../common/Styleguide/Common/Button';
import { ClearOutlined, Edit, VideoCallOutlined } from '@mui/icons-material';
import { StyledBodyText } from '../../../../../common/Styleguide/Common/Text';

export interface VideoUploadProps extends WithSession {
  onChange: (thumbnailLocation: string | null, originalLocation: string | null) => void;
  onUploadStatusChange?: (status: VideoUploadProgress) => void;
  slug: string;
  originalVideo: string | null;
  label: string | null;
  mandatory?: boolean;
  error?: string;
  videoContainerSize: {
    width: number;
    height: number;
  };
  isNonEditable?: boolean;
}

export type VideoUploadProgress = 'not-started' | 'uploading' | 'converting';

export interface VideuoUploadState {
  video: string | null;
  progress: VideoUploadProgress;
  error?: Error;
  hoveringVideo: boolean;
}

class VideoUpload extends React.Component<VideoUploadProps, VideuoUploadState> {
  private inputRef: React.RefObject<HTMLInputElement>;

  constructor(props: VideoUploadProps) {
    super(props);
    this.state = {
      progress: 'not-started',
      video: props.originalVideo,
      hoveringVideo: false,
    };
    this.inputRef = React.createRef();
  }

  render() {
    const { width, height } = this.props.videoContainerSize;
    const { label, error } = this.props;

    return (
      <div>
        {label && (
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
            <b>{label}</b>
          </Typography>
        )}
        <div
          style={{
            border: `2px dashed ${error ? COLORS.alert[100] : COLORS.black[30]}`,
            width: width,
            height: height,
            borderRadius: 8,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {this.renderPreview()}
        </div>
        {this.renderProgress()}
        {error && (
          <StyledBodyText sx={{ m: 0.5 }} variant="bodyLower2" color="error">
            {error}
          </StyledBodyText>
        )}
        <div style={{ display: 'none' }}>
          <input ref={this.inputRef} type="file" onChange={(event) => this.onFileInputChange(event)} />
        </div>
      </div>
    );
  }

  private renderPreview() {
    const { video } = this.state;
    if (!video) {
      return this.renderNoVideo();
    }
    const { videoCdnPrefix } = config.aws;
    // we assign key to the video tag, if not only the source
    // is rerendered, and the actual video does not change
    return (
      <>
        <video
          key={video}
          autoPlay={true}
          loop={true}
          muted={true}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          onMouseEnter={() => !this.props.isNonEditable && this.setState({ hoveringVideo: true })}
          onMouseLeave={() => !this.props.isNonEditable && this.setState({ hoveringVideo: false })}
        >
          <source src={`${videoCdnPrefix}/${video}`} type="video/mp4" />
        </video>
        <div
          // outer div that provides the dark overlay
          // it got pointer events set to none, so the video can be right clicked
          style={{
            position: 'absolute',
            inset: 0,
            opacity: this.state.hoveringVideo ? 1 : 0,
            transition: 'opacity 0.3s',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
          }}
        >
          <div
            // inner div that contains the buttons, takes only bottom right corner
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              pointerEvents: 'auto',
              padding: 8,
              gap: 8,
              zIndex: 1,
              display: this.props.isNonEditable ? 'none' : 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
            }}
            onMouseEnter={() => this.setState({ hoveringVideo: true })}
            onMouseLeave={() => this.setState({ hoveringVideo: false })}
          >
            {!this.props.mandatory && (
              <StyledButton
                variant="contained"
                color="white"
                onClick={this.onClearClicked}
                disabled={this.shouldDisableButtons()}
                height={34}
              >
                <ClearOutlined /> Clear
              </StyledButton>
            )}
            <StyledButton
              variant="contained"
              color="white"
              onClick={this.onUploadClicked}
              disabled={this.shouldDisableButtons()}
              height={34}
            >
              <Edit /> Update
            </StyledButton>
          </div>
        </div>
      </>
    );
  }

  private renderNoVideo() {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <StyledButton variant="contained" onClick={this.onUploadClicked} disabled={this.shouldDisableButtons()} height={34}>
          <VideoCallOutlined /> Upload
        </StyledButton>
      </div>
    );
  }

  private renderProgress() {
    const { progress, error } = this.state;
    if (error) {
      return this.renderError(error);
    }
    switch (progress) {
      case 'not-started':
        return;
      case 'uploading':
        return this.renderUploading();
      case 'converting':
        return this.renderConverting();
      default:
        return;
    }
  }

  private renderError(error: Error) {
    return <GraphQLError error={error} />;
  }

  private renderUploading() {
    return this.renderSpinner('uploading...');
  }

  private renderConverting() {
    return this.renderSpinner('converting...');
  }

  private renderSpinner(msg: string) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8, gap: 4 }}>
        <CircularProgress variant="indeterminate" size={16} />
        {msg}
      </div>
    );
  }

  private uploadVideo = async (video: File) => {
    const { session } = this.props;
    this.setState({ progress: 'uploading' });
    try {
      const uploads = await uploadFile(video, session, video.name);
      const uploadResponse = uploads[0];
      const uploadedFile: UploadedFile = {
        uploadId: uploadResponse.uploadId,
        path: video.name,
        size: video.size,
        temporaryUrl: '',
      };
      await this.convertVideo(uploadedFile);
    } catch (e) {
      this.setState({
        error: e as Error,
      });
    }
  };

  private convertVideo = async (file: UploadedFile | null) => {
    if (!file) {
      return this.handleFinishedConverting(null, null);
    }
    this.setState({
      progress: 'converting',
      error: undefined,
    });
    try {
      const res = await this.resizeVideo(file);
      const { thumbnail, original } = res;
      return this.handleFinishedConverting(thumbnail, original);
    } catch (e) {
      console.error('failed to resize video', e);
      if (e instanceof ApolloError || e instanceof Error) {
        this.setState({ error: e });
      } else {
        console.error(e);
      }
      this.setState({ progress: 'not-started' });
    }
  };

  private async resizeVideo(file: UploadedFile) {
    const { slug } = this.props;
    const { width, height } = this.props.videoContainerSize;
    const resp = await createVideoThumbnail({
      slug: slug,
      uploadId: file.uploadId,
      height,
      width,
    });
    return resp.data!.videoThumbnailCreate;
  }

  private onClearClicked = () => {
    const { onChange } = this.props;
    this.setState({ video: null });
    onChange(null, null);
  };

  private handleFinishedConverting(thumbnailLocation: string | null, originalLocation: string | null) {
    this.setState({
      progress: 'not-started',
      video: thumbnailLocation,
    });
    const { onChange } = this.props;
    onChange(thumbnailLocation, originalLocation);
  }

  private onUploadClicked = () => {
    const fileInput = this.getFileInput();
    // custom select file button -> manual click to activate element
    // *may not work* in IE/Safari - to be verified
    fileInput.click();
  };

  private onFileInputChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = evt.target;
    if (!files) {
      console.warn('Files is not set');
      return;
    }

    await this.uploadVideo(files[0]);

    // unset the value to allow to reselect the same file (does not trigger onChange otherwise)
    this.getFileInput().value = '';
  };

  private getFileInput(): HTMLInputElement {
    const input = this.inputRef.current;
    if (!input) {
      throw new Error('FileInput div ref is not set');
    }
    return input;
  }

  private shouldDisableButtons = () => {
    return this.state.progress !== 'not-started' || this.props.isNonEditable;
  };

  componentDidUpdate(_prevProps: VideoUploadProps, prevState: VideuoUploadState) {
    if (prevState.progress !== this.state.progress) {
      if (this.props.onUploadStatusChange) {
        this.props.onUploadStatusChange(this.state.progress);
      }
    }
  }
}

export default withSession<VideoUploadProps>(VideoUpload);
